import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '@/store/useStore';

export default function CartPage() {
  const navigate = useNavigate();
  const cart = useStore(state => state.cart);
  const updateQuantity = useStore(state => state.updateQuantity);
  const removeFromCart = useStore(state => state.removeFromCart);
  const cartTotal = useStore(state => state.cartTotal());

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-warm flex items-center justify-center">
        <div className="text-center">
          <svg className="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <h2 className="text-2xl font-serif font-bold text-slate mb-2">Your basket is empty</h2>
          <p className="text-gray-600 mb-6">Start adding gear to your basket</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-forest text-warm font-semibold rounded-lg hover:bg-forest-light transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-serif font-bold text-slate mb-8">Shopping Basket</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => {
              const price = item.product.salePrice || item.product.price;
              const stockKey = item.size ? `${item.colour}-${item.size}` : item.colour;
              const availableStock = item.product.stock[stockKey] || 0;

              return (
                <div key={`${item.product.id}-${item.colour}-${item.size}`} className="bg-white rounded-lg p-6 flex gap-6">
                  <img
                    src={item.product.imageUrl}
                    alt={item.product.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />

                  <div className="flex-1">
                    <h3 className="font-semibold text-slate mb-1">{item.product.name}</h3>
                    <p className="text-sm text-gray-500 mb-2">{item.product.brand}</p>

                    <div className="flex gap-4 text-sm text-gray-600 mb-3">
                      <span>Colour: {item.colour}</span>
                      {item.size && <span>Size: {item.size}</span>}
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.colour, item.size, item.quantity - 1)}
                          className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-100"
                        >
                          -
                        </button>
                        <span className="w-12 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.colour, item.size, Math.min(availableStock, item.quantity + 1))}
                          className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-100"
                          disabled={item.quantity >= availableStock}
                        >
                          +
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.product.id, item.colour, item.size)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="text-lg font-bold text-slate">£{(price * item.quantity).toFixed(2)}</p>
                    {item.product.salePrice && (
                      <p className="text-sm text-gray-500 line-through">£{(item.product.price * item.quantity).toFixed(2)}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-6 sticky top-24">
              <h2 className="text-xl font-semibold text-slate mb-4">Order Summary</h2>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>£{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span>{cartTotal >= 50 ? 'FREE' : '£4.95'}</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between font-bold text-lg text-slate">
                    <span>Total</span>
                    <span>£{(cartTotal >= 50 ? cartTotal : cartTotal + 4.95).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {cartTotal < 50 && (
                <p className="text-sm text-amber mb-4">
                  Add £{(50 - cartTotal).toFixed(2)} more for free shipping
                </p>
              )}

              <button
                onClick={() => navigate('/checkout')}
                className="w-full px-6 py-3 bg-forest text-warm font-semibold rounded-lg hover:bg-forest-light transition-colors mb-3"
              >
                Proceed to Checkout
              </button>

              <Link
                to="/"
                className="block text-center text-sm text-amber hover:text-amber-dark"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
